import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../styles/LanguageTypography.css';

import {
    faShoppingCart, faUser, faPhone, faMapMarkerAlt, faCalendarAlt, faBox, faRupeeSign, faTag,
    faBalanceScale, faDollarSign, faCalculator, faExclamationTriangle, faPlus, faMinus, faTrash, faSave, faTimes, faTrashAlt,
    faSpinner, faCheckCircle, faExclamationCircle, faSearch, faBarcode, faQrcode, faCreditCard, faMoneyBill,
    faUniversity, faWallet, faPercent, faReceipt, faChevronDown, faChevronUp, faBoxes, faHistory, faUndo, faRedo,
    faPrint, faFilePdf, faDownload, faUpload, faEdit, faChartLine, faTimesCircle, faShoppingBag, faAddressCard,
    faIdCard, faKey, faLock, faUnlock, faEye, faEyeSlash, faStore, faEnvelope, faGlobe, faBuilding, faFileAlt,
    faTruck, faWeightHanging, faCubes, faLayerGroup, faToggleOn, faToggleOff, faCopy, faList, faUsers, faBuilding as faCompany,
    faRuler, faPalette
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// API Endpoints
// ==============================================
const API_BASE = 'https://ourzestapp.online/oz_backend/mechanics_module';
const SELL_API = 'https://ourzestapp.online/oz_backend/solar_module/solar_sell.php';
const CUSTOMER_API = 'https://ourzestapp.online/oz_backend/mechanics_module/mechanics_customers.php';
const PRODUCT_API = 'https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php';
const RTL_LANGUAGE_CODES = new Set(['ur', 'ps', 'ar', 'fa', 'prs']);

const sourceValue = (record, field, fallback = '') =>
    record?._original?.[field] ?? record?.[field] ?? fallback;

const parseJsonResponse = async (response, endpointName = 'API') => {
    const body = await response.text();

    if (!body.trim()) {
        throw new Error(
            `${endpointName} returned an empty response (HTTP ${response.status}). ` +
            'Check the PHP error log and make sure the endpoint always echoes JSON.'
        );
    }

    let data;
    try {
        data = JSON.parse(body);
    } catch (error) {
        console.error(`${endpointName} returned non-JSON:`, body);
        throw new Error(
            `${endpointName} returned invalid JSON (HTTP ${response.status}): ` +
            body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 250)
        );
    }

    if (!response.ok) {
        throw new Error(data.message || `${endpointName} request failed (HTTP ${response.status})`);
    }

    return data;
};

const computeAvailableStock = (product) => {
    return parseFloat(product.quantity) || 0;
};

const getProductSellPrice = (product) => {
    if (product.sell_per_pack && parseFloat(product.sell_per_pack) > 0) {
        return parseFloat(product.sell_per_pack);
    }
    if (product.sell_price_per_unit && parseFloat(product.sell_price_per_unit) > 0) {
        return parseFloat(product.sell_price_per_unit);
    }
    return parseFloat(product.sell_price) || 0;
};

const buildSaleItem = (product, currentShopId) => {
    const price = getProductSellPrice(product) || 0;
    const availableStock = computeAvailableStock(product);
    const isCentralized = product.is_product_centralized === 1;
    const ownerShopId = product.owner_shop_id || null;

    return {
        product_id: product.id,
        product_name: product.product_name || '',
        product_code: product.product_code || '',
        batch_no: product.batch_no || '',
        barcode: product.barcode || '',
        warranty: product.warranty || '',
        product_warranty: product.warranty || '',
        issue_warranty: false,
        WarrantyStatus: 0,
        warranty_issuance_date: '',
        price: price,
        quantity: '1',
        available_stock: availableStock,
        quantity_alert: parseFloat(product.quantity_alert) || 5,
        total: String(Math.round(price * 1)),
        sell_method: product.sell_method || 'sub_packing',
        packing_type: product.packing_type || null,
        sub_packing_type: product.sub_packing_type || null,
        items_per_pack: parseFloat(product.items_per_pack) || 1,
        unit_of_measure: product.unit_of_measure || 'piece',
        is_centralized: isCentralized ? 1 : 0,
        centralized: product.centralized || '',
        owner_shop_id: ownerShopId,
        is_owner_shop: ownerShopId == currentShopId,
        supplier_name: product.supplier_name || '',
        company: product.company || '',
        original_language: product.original_language || 'en',
        _original: product._original || {},
        _translation: product._translation || null,
        gunit: product.gunit || product.unit_of_measure || '',
        size: product.size || '',
        gauge: product.gauge || '',
        weight: product.weight || '',
        class: product.class || ''
    };
};

// ==============================================
// BARCODE SCANNER HOOK
// ==============================================
const useBarcodeScanner = (onBarcodeScanned, isActive = true) => {
    const bufferRef = useRef('');
    const lastTimeRef = useRef(0);
    const scanTimerRef = useRef(null);
    const isProcessingRef = useRef(false);
    const isLikelyScanningRef = useRef(false);
    const leakSnapshotRef = useRef(null);

    useEffect(() => {
        if (!isActive) return;

        const SCAN_MAX_INTERVAL_MS = 40;
        const MIN_BARCODE_LENGTH = 4;
        const BUFFER_TIMEOUT_MS = 150;

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value'
        )?.set;

        const setNativeValue = (el, value) => {
            if (!el) return;
            if (el.tagName === 'INPUT' && nativeInputValueSetter) {
                nativeInputValueSetter.call(el, value);
            } else {
                el.value = value;
            }
            el.dispatchEvent(new Event('input', { bubbles: true }));
        };

        const restoreLeak = () => {
            const snap = leakSnapshotRef.current;
            if (snap && snap.element && snap.element.isConnected) {
                setNativeValue(snap.element, snap.value);
                if (snap.selectionStart != null && snap.element.setSelectionRange) {
                    try {
                        snap.element.setSelectionRange(snap.selectionStart, snap.selectionStart);
                    } catch (e) { }
                }
            }
            leakSnapshotRef.current = null;
        };

        const resetBuffer = () => {
            bufferRef.current = '';
            isLikelyScanningRef.current = false;
            leakSnapshotRef.current = null;
        };

        const handleKeyDown = (e) => {
            const key = typeof e?.key === 'string' ? e.key : '';
            if (!key) {
                resetBuffer();
                return;
            }

            if (e.ctrlKey || e.metaKey || e.altKey) {
                resetBuffer();
                return;
            }
            if (key === 'Shift' || key === 'CapsLock' || key === 'Tab') {
                return;
            }

            const now = Date.now();
            const delta = now - lastTimeRef.current;
            lastTimeRef.current = now;

            const target = e.target;
            const isEditable = !!target && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            );

            if (key === 'Enter') {
                clearTimeout(scanTimerRef.current);
                const candidate = String(bufferRef.current || '').trim();
                const isConfirmedScan = isLikelyScanningRef.current && candidate.length >= MIN_BARCODE_LENGTH;

                if (isConfirmedScan) {
                    e.preventDefault();
                    e.stopPropagation();
                    restoreLeak();

                    if (!isProcessingRef.current) {
                        isProcessingRef.current = true;
                        onBarcodeScanned(candidate);
                        setTimeout(() => { isProcessingRef.current = false; }, 500);
                    }
                }
                resetBuffer();
                return;
            }

            if (key.length !== 1) {
                resetBuffer();
                return;
            }

            if (String(bufferRef.current || '').length === 0 || delta > SCAN_MAX_INTERVAL_MS) {
                bufferRef.current = key;
                isLikelyScanningRef.current = false;
                leakSnapshotRef.current = isEditable ? {
                    element: target,
                    value: target.value,
                    selectionStart: target.selectionStart
                } : null;
            } else {
                bufferRef.current = String(bufferRef.current || '') + key;
                isLikelyScanningRef.current = true;
                if (isEditable) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }

            clearTimeout(scanTimerRef.current);
            scanTimerRef.current = setTimeout(resetBuffer, BUFFER_TIMEOUT_MS);
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            clearTimeout(scanTimerRef.current);
        };
    }, [onBarcodeScanned, isActive]);
};

// ==============================================
// STATIC TRANSLATIONS (EXACTLY like SolarSell.js)
// ==============================================
const sellTranslations = {
    en: {
        newSale: 'New Sale',
        editSale: 'Edit Sale',
        saleDetails: 'Sale Details',
        sellProducts: 'Sell Products',
        bn: 'BN',
        date: 'Date',
        customerDetails: 'Customer Details',
        customerName: 'Customer Name',
        customerPhone: 'Phone Number',
        customerAddress: 'Address',
        searchProducts: 'Search Products',
        productName: 'Product',
        productCode: 'Product Code',
        batch: 'Batch',
        barcode: 'Barcode',
        price: 'Price',
        quantity: 'Qty',
        total: 'Total',
        remove: 'Remove',
        stock: 'Stock',
        totalStockQuantity: 'Total Stock Quantity',
        srNo: 'Sr#',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        completeSale: 'Save & Close',
        updateSale: 'Update Sale',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        saleCompleted: 'Sale completed successfully!',
        saleUpdated: 'Sale updated successfully!',
        failedSave: 'Failed to save sale',
        failedUpdate: 'Failed to update sale',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected',
        itemsRequired: 'At least one item is required',
        quantityExceedsStock: 'Quantity exceeds available stock',
        invalidQuantity: 'Please enter a valid quantity',
        invalidPrice: 'Please enter a valid price',
        productRequired: 'Please select a product',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        addItem: 'Add Item',
        saveDraft: 'Save Draft',
        drafts: 'Drafts',
        savedDrafts: 'Saved Drafts',
        noDraftsSaved: 'No drafts saved',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        clearForm: 'Clear Form',
        viewDrafts: 'View Drafts',
        goBack: 'Go Back',
        receipt: 'Receipt',
        printReceipt: 'Print Receipt',
        thankYou: 'Thank you for your purchase!',
        poweredBy: 'Powered by OurZest',
        scannerReady: 'Scanner Ready',
        productFound: 'Product found!',
        productNotFound: 'Product not found',
        barcodeSet: 'Barcode set',
        customer: 'Customer',
        optional: 'Optional',
        supplier: 'Supplier',
        company: 'Company',
        centralized: 'Centralized',
        local: 'Local',
        owner: 'Owner',
        shared: 'Shared',
        type: 'Type',
        unit: 'Unit',
        size: 'Size',
        gauge: 'Gauge',
        weight: 'Weight',
        class: 'Class',
        warranty: 'Warranty',
        issuanceDate: 'Issuance Date',
        salesReceipt: 'Sales Receipt',
        dateTime: 'Date & Time',
        previousBalance: 'Previous Balance',
        customerSelected: 'Customer selected'
    },
    ur: {
        newSale: 'نیا سیل',
        editSale: 'سیل میں ترمیم کریں',
        saleDetails: 'سیل کی تفصیلات',
        sellProducts: 'پروڈکٹس بیچیں',
        bn: 'بی این',
        date: 'تاریخ',
        customerDetails: 'کسٹمر کی تفصیلات',
        customerName: 'کسٹمر کا نام',
        customerPhone: 'فون نمبر',
        customerAddress: 'پتہ',
        searchProducts: 'پروڈکٹس تلاش کریں',
        productName: 'پروڈکٹ',
        productCode: 'پروڈکٹ کوڈ',
        batch: 'بیچ',
        barcode: 'بارکوڈ',
        price: 'قیمت',
        quantity: 'مقدار',
        total: 'کل',
        remove: 'ہٹائیں',
        stock: 'اسٹاک',
        totalStockQuantity: 'کل اسٹاک مقدار',
        srNo: 'شمارہ',
        subtotal: 'ذیلی کل',
        tax: 'ٹیکس',
        discount: 'ڈسکاؤنٹ',
        grandTotal: 'کل رقم',
        paidAmount: 'ادا کردہ رقم',
        balance: 'باقی رقم',
        paymentMethod: 'ادائیگی کا طریقہ',
        cash: 'نقد',
        card: 'کارڈ',
        upi: 'یوپی آئی',
        completeSale: 'سیل مکمل کریں',
        updateSale: 'سیل اپ ڈیٹ کریں',
        clear: 'صاف کریں',
        saving: 'محفوظ ہو رہا ہے...',
        loading: 'لوڈ ہو رہا ہے...',
        saleCompleted: 'سیل کامیابی سے مکمل ہو گیا!',
        saleUpdated: 'سیل کامیابی سے اپ ڈیٹ ہو گیا!',
        failedSave: 'سیل محفوظ کرنے میں ناکامی',
        failedUpdate: 'سیل اپ ڈیٹ کرنے میں ناکامی',
        networkError: 'نیٹ ورک کی خرابی',
        noShopSelected: 'کوئی دکان منتخب نہیں',
        itemsRequired: 'کم از کم ایک آئٹم ضروری ہے',
        quantityExceedsStock: 'مقدار اسٹاک سے زیادہ ہے',
        invalidQuantity: 'براہ کرم درست مقدار درج کریں',
        invalidPrice: 'براہ کرم درست قیمت درج کریں',
        productRequired: 'براہ کرم ایک پروڈکٹ منتخب کریں',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        addItem: 'آئٹم شامل کریں',
        saveDraft: 'ڈرافٹ محفوظ کریں',
        drafts: 'ڈرافٹس',
        savedDrafts: 'محفوظ کردہ ڈرافٹس',
        noDraftsSaved: 'کوئی ڈرافٹ محفوظ نہیں',
        draftLoaded: 'ڈرافٹ لوڈ ہو گیا',
        draftCleared: 'ڈرافٹ صاف ہو گیا',
        clearForm: 'فارم صاف کریں',
        viewDrafts: 'ڈرافٹس دیکھیں',
        goBack: 'واپس جائیں',
        receipt: 'رسید',
        printReceipt: 'رسید پرنٹ کریں',
        thankYou: 'آپ کے خریداری کا شکریہ!',
        poweredBy: 'OurZest کے ذریعے تقویت یافتہ',
        scannerReady: 'اسکینر تیار ہے',
        productFound: 'پروڈکٹ مل گیا!',
        productNotFound: 'پروڈکٹ نہیں ملا',
        barcodeSet: 'بارکوڈ سیٹ ہو گیا',
        customer: 'کسٹمر',
        optional: 'اختیاری',
        supplier: 'سپلائر',
        company: 'کمپنی',
        centralized: 'مرکزی',
        local: 'مقامی',
        owner: 'مالک',
        shared: 'مشترکہ',
        type: 'قسم',
        unit: 'یونٹ',
        size: 'سائز',
        gauge: 'گیج',
        weight: 'وزن',
        class: 'کلاس',
        warranty: 'وارنٹی',
        issuanceDate: 'اجراء کی تاریخ',
        salesReceipt: 'سیل رسید',
        dateTime: 'تاریخ اور وقت',
        previousBalance: 'پچھلا بیلنس',
        customerSelected: 'کسٹمر منتخب ہو گیا'
    },
    ar: {
        newSale: 'بيع جديد',
        editSale: 'تعديل البيع',
        saleDetails: 'تفاصيل البيع',
        sellProducts: 'بيع المنتجات',
        bn: 'BN',
        date: 'التاريخ',
        customerDetails: 'تفاصيل العميل',
        customerName: 'اسم العميل',
        customerPhone: 'رقم الهاتف',
        customerAddress: 'العنوان',
        searchProducts: 'البحث عن منتجات',
        productName: 'المنتج',
        productCode: 'رمز المنتج',
        batch: 'الدفعة',
        barcode: 'الباركود',
        price: 'السعر',
        quantity: 'الكمية',
        total: 'المجموع',
        remove: 'إزالة',
        stock: 'المخزون',
        totalStockQuantity: 'إجمالي كمية المخزون',
        srNo: '#',
        subtotal: 'المجموع الفرعي',
        tax: 'الضريبة',
        discount: 'الخصم',
        grandTotal: 'المجموع الكلي',
        paidAmount: 'المبلغ المدفوع',
        balance: 'الرصيد',
        paymentMethod: 'طريقة الدفع',
        cash: 'نقدي',
        card: 'بطاقة',
        upi: 'يو بي آي',
        completeSale: 'إتمام البيع',
        updateSale: 'تحديث البيع',
        clear: 'مسح',
        saving: 'جاري الحفظ...',
        loading: 'جاري التحميل...',
        saleCompleted: 'تم إتمام البيع بنجاح!',
        saleUpdated: 'تم تحديث البيع بنجاح!',
        failedSave: 'فشل حفظ البيع',
        failedUpdate: 'فشل تحديث البيع',
        networkError: 'حدث خطأ في الشبكة',
        noShopSelected: 'لم يتم اختيار متجر',
        itemsRequired: 'مطلوب عنصر واحد على الأقل',
        quantityExceedsStock: 'الكمية تتجاوز المخزون المتاح',
        invalidQuantity: 'الرجاء إدخال كمية صحيحة',
        invalidPrice: 'الرجاء إدخال سعر صحيح',
        productRequired: 'الرجاء اختيار منتج',
        success: 'نجاح',
        error: 'خطأ',
        warning: 'تحذير',
        addItem: 'إضافة عنصر',
        saveDraft: 'حفظ المسودة',
        drafts: 'المسودات',
        savedDrafts: 'المسودات المحفوظة',
        noDraftsSaved: 'لا توجد مسودات محفوظة',
        draftLoaded: 'تم تحميل المسودة',
        draftCleared: 'تم مسح المسودة',
        clearForm: 'مسح النموذج',
        viewDrafts: 'عرض المسودات',
        goBack: 'العودة',
        receipt: 'إيصال',
        printReceipt: 'طباعة الإيصال',
        thankYou: 'شكراً لشرائك!',
        poweredBy: 'مدعوم من OurZest',
        scannerReady: 'الماسح جاهز',
        productFound: 'تم العثور على المنتج!',
        productNotFound: 'المنتج غير موجود',
        barcodeSet: 'تم تعيين الباركود',
        customer: 'العميل',
        optional: 'اختياري',
        supplier: 'المورد',
        company: 'الشركة',
        centralized: 'مركزي',
        local: 'محلي',
        owner: 'مالك',
        shared: 'مشترك',
        type: 'نوع',
        unit: 'وحدة',
        size: 'حجم',
        gauge: 'مقياس',
        weight: 'وزن',
        class: 'فئة',
        warranty: 'ضمان',
        issuanceDate: 'تاريخ الإصدار',
        salesReceipt: 'إيصال البيع',
        dateTime: 'التاريخ والوقت',
        previousBalance: 'الرصيد السابق',
        customerSelected: 'تم اختيار العميل'
    }
};

// ==============================================
// TOAST NOTIFICATION
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };
    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };
    if (!toast.show) return null;
    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// SALE PAYMENT SECTION (EXACT same as SolarSell.js)
// ==============================================
const useSalePayments = ({ apiUrl, shopId, username, customerId, invoiceTotal }) => {
    const [payments, setPayments] = useState([]);
    const [paidTotal, setPaidTotal] = useState(0);
    const [previousBalance, setPreviousBalance] = useState(0);
    const [creditBalance, setCreditBalance] = useState(0);
    const [customerCreditApplied, setCustomerCreditApplied] = useState(0);

    const addPayment = (payment) => {
        setPayments([...payments, payment]);
        setPaidTotal(prev => prev + (payment.amount || 0));
    };

    const removePayment = (index) => {
        const removed = payments[index];
        setPayments(payments.filter((_, i) => i !== index));
        setPaidTotal(prev => prev - (removed?.amount || 0));
    };

    const resetPayments = () => {
        setPayments([]);
        setPaidTotal(0);
    };

    const validate = () => {
        if (invoiceTotal > 0 && paidTotal < invoiceTotal) {
            return 'Total paid amount must equal or exceed the invoice total';
        }
        return null;
    };

    return {
        payments,
        paidTotal,
        previousBalance,
        creditBalance,
        customerCreditApplied,
        addPayment,
        removePayment,
        resetPayments,
        validate,
        payload: {
            payments: payments,
            paid_total: paidTotal,
            previous_balance: previousBalance,
            credit_balance: creditBalance,
            customer_credit_applied: customerCreditApplied
        }
    };
};

const SalePaymentSection = ({ payment, subtotal, discount, onDiscountChange, disabled, translations }) => {
    return (
        <div className="ozui-payment-section">
            <div className="ozui-payment-header">
                <h4><FontAwesomeIcon icon={faWallet} /> {translations?.paymentMethod || 'Payment'}</h4>
            </div>
            <div className="ozui-payment-body">
                <div className="ozui-payment-row">
                    <span className="ozui-payment-label">{translations?.subtotal || 'Subtotal'}:</span>
                    <span className="ozui-payment-value">Rs. {subtotal || 0}</span>
                </div>
                <div className="ozui-payment-row">
                    <span className="ozui-payment-label">{translations?.discount || 'Discount'}:</span>
                    <input 
                        type="number" 
                        name="discount" 
                        value={discount || 0} 
                        onChange={onDiscountChange} 
                        step="1" 
                        min="0" 
                        disabled={disabled} 
                        style={{ width: '100px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #e5e7eb' }} 
                        data-no-scan="true"
                    />
                </div>
                <div className="ozui-payment-row ozui-payment-total">
                    <span className="ozui-payment-label">{translations?.grandTotal || 'Grand Total'}:</span>
                    <span className="ozui-payment-value">Rs. {Math.round((parseFloat(subtotal) || 0) - (parseFloat(discount) || 0))}</span>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// ITEMS TABLE COMPONENT
// ==============================================
const ItemsTable = ({
    items, onItemChange, onSelectProductForRow, onRemove, onAdd, errors, disabled, translations, isRTL, searchProductsAPI, showToast, currentShop
}) => {
    const [filteredByIndex, setFilteredByIndex] = useState({});
    const [searchTerms, setSearchTerms] = useState({});
    const [showDropdown, setShowDropdown] = useState({});
    const [searching, setSearching] = useState({});
    const rowRefs = useRef({});
    const searchDebounceRef = useRef({});

    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(rowRefs.current).forEach(index => {
                if (rowRefs.current[index] && !rowRefs.current[index].contains(event.target)) {
                    setShowDropdown(prev => ({ ...prev, [index]: false }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleRowSearch = (index, term) => {
        setSearchTerms(prev => ({ ...prev, [index]: term }));
        if (searchDebounceRef.current[index]) clearTimeout(searchDebounceRef.current[index]);
        if (!term || term.trim().length < 2) {
            setFilteredByIndex(prev => ({ ...prev, [index]: [] }));
            setShowDropdown(prev => ({ ...prev, [index]: false }));
            return;
        }
        searchDebounceRef.current[index] = setTimeout(async () => {
            setSearching(prev => ({ ...prev, [index]: true }));
            const results = await searchProductsAPI(term);
            setFilteredByIndex(prev => ({ ...prev, [index]: results }));
            setShowDropdown(prev => ({ ...prev, [index]: results.length > 0 }));
            setSearching(prev => ({ ...prev, [index]: false }));
        }, 250);
    };

    const selectProduct = (index, product) => {
        onSelectProductForRow(index, product);
        setSearchTerms(prev => ({ ...prev, [index]: product.product_name || '' }));
        setFilteredByIndex(prev => ({ ...prev, [index]: [] }));
        setShowDropdown(prev => ({ ...prev, [index]: false }));
        if (showToast) showToast('success', `"${product.product_name}" added to sale`);
    };

    return (
        <div className="ozui-items-table-container">
            <div className="ozui-table-header-actions">
                <h4><FontAwesomeIcon icon={faBoxes} />{translations?.sellProducts || 'Items'}</h4>
                <button type="button" className="ozui-btn-add-item" onClick={onAdd} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} /><span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="ozui-table-wrapper">
                <table className="ozui-items-table">
                    <thead>
                        <tr>
                            <th style={{ width: '3%', minWidth: '30px' }}>{translations?.srNo || 'Sr#'}</th>
                            <th style={{ width: '5%', minWidth: '55px' }}>{translations?.batch || 'Batch'}</th>
                            <th style={{ width: '5%', minWidth: '55px' }}>{translations?.barcode || 'Barcode'}</th>
                            <th style={{ width: '12%', minWidth: '110px' }}>{translations?.productName || 'Product'} <span className="ozui-required">*</span></th>
                            <th style={{ width: '4%', minWidth: '45px' }}>{translations?.unit || 'Unit'}</th>
                            <th style={{ width: '4%', minWidth: '45px' }}>{translations?.size || 'Size'}</th>
                            <th style={{ width: '4%', minWidth: '45px' }}>{translations?.gauge || 'Gauge'}</th>
                            <th style={{ width: '4%', minWidth: '45px' }}>{translations?.weight || 'Weight'}</th>
                            <th style={{ width: '4%', minWidth: '45px' }}>{translations?.class || 'Class'}</th>
                            <th style={{ width: '7%', minWidth: '65px' }}>{translations?.warranty || 'Warranty'}</th>
                            <th style={{ width: '7%', minWidth: '70px' }}>{translations?.issuanceDate || 'Issuance'}</th>
                            <th style={{ width: '6%', minWidth: '55px' }}>{translations?.price || 'Price'} <span className="ozui-required">*</span></th>
                            <th style={{ width: '6%', minWidth: '50px' }}>{translations?.quantity || 'Qty'} <span className="ozui-required">*</span></th>
                            <th style={{ width: '7%', minWidth: '65px' }}>{translations?.total || 'Total'}</th>
                            <th style={{ width: '6%', minWidth: '55px' }}>{translations?.stock || 'Stock'}</th>
                            <th style={{ width: '4%', minWidth: '35px' }}>{translations?.type || 'Type'}</th>
                            <th style={{ width: '3%', minWidth: '30px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const filteredForIndex = filteredByIndex[index] || [];
                            const showDrop = showDropdown[index] || false;
                            const searchTerm = searchTerms[index] !== undefined ? searchTerms[index] : item.product_name;
                            const isLowStock = parseFloat(item.available_stock || 0) <= (item.quantity_alert || 5);
                            const isCentralized = item.is_centralized === 1;
                            const isOwner = item.is_owner_shop || false;
                            const availableWarranty = String(item.product_warranty || item.warranty || '').trim();
                            const isWarrantyIssued = Boolean(
                                availableWarranty && Number(item.WarrantyStatus ?? (item.issue_warranty ? 1 : 0)) === 1
                            );

                            return (
                                <tr key={index} className={errors[`quantity_${index}`] || errors[`product_${index}`] ? 'ozui-error-row' : ''}>
                                    <td>
                                        <div className="ozui-table-field" style={{ textAlign: 'center', fontWeight: 'bold', minWidth: '25px' }}>
                                            {index + 1}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="text" value={item.batch_no || ''} onChange={e => onItemChange(index, 'batch_no', e.target.value)} disabled={disabled} placeholder="-" data-no-scan="true" style={{ minWidth: '45px', width: '100%', padding: '4px 6px', fontSize: '11px', outline: 'none', backgroundColor: '#f9fafb', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #e5e7eb' }} readOnly />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="text" value={item.barcode || ''} onChange={e => onItemChange(index, 'barcode', e.target.value)} disabled={disabled} placeholder="-" data-no-scan="true" style={{ minWidth: '45px', width: '100%', padding: '4px 6px', fontSize: '11px', outline: 'none', backgroundColor: '#f9fafb', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #e5e7eb' }} readOnly />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field" ref={el => rowRefs.current[index] = el}>
                                            <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                                                <input 
                                                    type="text" 
                                                    value={searchTerm} 
                                                    onChange={e => handleRowSearch(index, e.target.value)} 
                                                    onFocus={() => searchTerm && searchTerm.length >= 2 && setShowDropdown(prev => ({ ...prev, [index]: filteredForIndex.length > 0 }))} 
                                                    placeholder={translations?.searchProducts || 'Search product...'} 
                                                    disabled={disabled} 
                                                    autoComplete="off" 
                                                    data-no-scan="true" 
                                                    className={errors[`product_${index}`] ? 'ozui-error' : ''} 
                                                    style={{ minWidth: '100px', width: '100%', padding: '4px 8px', fontSize: '12px', outline: 'none', backgroundColor: '#fff', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: errors[`product_${index}`] ? '1px solid #dc2626' : '1px solid #e5e7eb' }} 
                                                />
                                                {searching[index] && (
                                                    <FontAwesomeIcon icon={faSpinner} spin style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '12px' }} />
                                                )}
                                                {showDrop && filteredForIndex.length > 0 && (
                                                    <div className="ozui-product-dropdown-table" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, backgroundColor: '#fff', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', maxHeight: '300px', overflowY: 'auto', marginTop: '4px', minWidth: '280px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                                        {filteredForIndex.map(product => {
                                                            const stock = computeAvailableStock(product);
                                                            const lowStock = stock <= (product.quantity_alert || 5);
                                                            const isCentralized = product.is_product_centralized === 1;
                                                            const isOwner = product.owner_shop_id == currentShop?.shop_id;
                                                            const supplierName = product.supplier_name || '';
                                                            const companyName = product.company || '';

                                                            return (
                                                                <div key={product.id} className="ozui-product-option" onClick={() => selectProduct(index, product)} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', transition: 'background-color 0.15s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                                    <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                        {product.product_name}
                                                                        {product.warranty && (
                                                                            <span title={`Warranty: ${product.warranty}`} style={{ fontSize: '9px', background: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: '8px', fontWeight: '600' }}>
                                                                                <FontAwesomeIcon icon={faCheckCircle} /> Warranty
                                                                            </span>
                                                                        )}
                                                                        {isCentralized ? (
                                                                            <span style={{
                                                                                fontSize: '8px',
                                                                                background: isOwner ? '#10b981' : '#6366f1',
                                                                                color: 'white',
                                                                                padding: '1px 6px',
                                                                                borderRadius: '8px',
                                                                                fontWeight: '600',
                                                                                whiteSpace: 'nowrap'
                                                                            }}>
                                                                                {isOwner ? '⭐ Owner' : '🌐 Shared'}
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{
                                                                                fontSize: '8px',
                                                                                background: '#e5e7eb',
                                                                                color: '#6b7280',
                                                                                padding: '1px 6px',
                                                                                borderRadius: '8px',
                                                                                fontWeight: '600',
                                                                                whiteSpace: 'nowrap'
                                                                            }}>
                                                                                Local
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {product.warranty && <div style={{ color: '#15803d', fontSize: '10px', fontWeight: '600', marginTop: '2px' }}>✓ {product.warranty}</div>}
                                                                    <div style={{ display: 'flex', gap: '10px', fontSize: '11px', color: '#6b7280', flexWrap: 'wrap', marginBottom: '3px' }}>
                                                                        {product.batch_no && (<span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><FontAwesomeIcon icon={faTag} style={{ fontSize: '9px' }} />{product.batch_no}</span>)}
                                                                        {product.barcode && (<span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><FontAwesomeIcon icon={faBarcode} style={{ fontSize: '9px' }} />{product.barcode}</span>)}
                                                                        {product.gunit && (<span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><FontAwesomeIcon icon={faRuler} style={{ fontSize: '9px' }} />{product.gunit}</span>)}
                                                                        {product.size && (<span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>Size: {product.size}</span>)}
                                                                        {product.gauge && (<span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>Gauge: {product.gauge}</span>)}
                                                                        {product.weight && (<span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>Wt: {product.weight}</span>)}
                                                                        {product.class && (<span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>Cls: {product.class}</span>)}
                                                                        {(supplierName || companyName) && (
                                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#059669' }}>
                                                                                <FontAwesomeIcon icon={faUsers} style={{ fontSize: '9px' }} />
                                                                                {supplierName || companyName}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div style={{ display: 'flex', gap: '14px', fontSize: '11px', flexWrap: 'wrap' }}>
                                                                        <span style={{ color: '#059669', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                                            <FontAwesomeIcon icon={faRupeeSign} style={{ fontSize: '9px' }} />
                                                                            {Math.round(getProductSellPrice(product))}
                                                                            <span style={{ fontWeight: '400', color: '#6b7280', fontSize: '9px' }}>
                                                                                {product.sell_method === 'packing' ? '/pack' : '/unit'}
                                                                            </span>
                                                                        </span>
                                                                        <span style={{ color: lowStock ? '#dc2626' : '#6b7280', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                                            <FontAwesomeIcon icon={faBox} style={{ fontSize: '9px' }} />
                                                                            Stock: {Math.round(stock)}
                                                                            {isCentralized && !isOwner && (
                                                                                <span style={{ fontSize: '8px', color: '#6366f1', marginLeft: '2px' }}>
                                                                                    (shared)
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                            {errors[`product_${index}`] && (<div className="ozui-table-error" style={{ color: '#dc2626', fontSize: '11px', marginTop: '3px' }}>{errors[`product_${index}`]}</div>)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input 
                                                type="text" 
                                                value={item.gunit || item.unit_of_measure || ''} 
                                                onChange={e => onItemChange(index, 'gunit', e.target.value)} 
                                                disabled={disabled} 
                                                placeholder="-" 
                                                style={{ minWidth: '35px', width: '100%', padding: '4px 4px', fontSize: '10px', outline: 'none', backgroundColor: '#f9fafb', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #e5e7eb' }} 
                                                readOnly 
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input 
                                                type="text" 
                                                value={item.size || ''} 
                                                onChange={e => onItemChange(index, 'size', e.target.value)} 
                                                disabled={disabled} 
                                                placeholder="-" 
                                                style={{ minWidth: '35px', width: '100%', padding: '4px 4px', fontSize: '10px', outline: 'none', backgroundColor: '#f9fafb', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #e5e7eb' }} 
                                                readOnly 
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input 
                                                type="text" 
                                                value={item.gauge || ''} 
                                                onChange={e => onItemChange(index, 'gauge', e.target.value)} 
                                                disabled={disabled} 
                                                placeholder="-" 
                                                style={{ minWidth: '35px', width: '100%', padding: '4px 4px', fontSize: '10px', outline: 'none', backgroundColor: '#f9fafb', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #e5e7eb' }} 
                                                readOnly 
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input 
                                                type="text" 
                                                value={item.weight || ''} 
                                                onChange={e => onItemChange(index, 'weight', e.target.value)} 
                                                disabled={disabled} 
                                                placeholder="-" 
                                                style={{ minWidth: '35px', width: '100%', padding: '4px 4px', fontSize: '10px', outline: 'none', backgroundColor: '#f9fafb', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #e5e7eb' }} 
                                                readOnly 
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input 
                                                type="text" 
                                                value={item.class || ''} 
                                                onChange={e => onItemChange(index, 'class', e.target.value)} 
                                                disabled={disabled} 
                                                placeholder="-" 
                                                style={{ minWidth: '35px', width: '100%', padding: '4px 4px', fontSize: '10px', outline: 'none', backgroundColor: '#f9fafb', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #e5e7eb' }} 
                                                readOnly 
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        {availableWarranty ? (
                                            <label className={`ozui-warranty-toggle ${isWarrantyIssued ? 'is-issued' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={isWarrantyIssued}
                                                    onChange={event => onItemChange(index, 'issue_warranty', event.target.checked)}
                                                    disabled={disabled}
                                                    data-no-scan="true"
                                                />
                                                <span>{isWarrantyIssued ? 'Issued' : 'Issue'}</span>
                                                <small>{availableWarranty}</small>
                                            </label>
                                        ) : (
                                            <span className="ozui-no-warranty">No warranty</span>
                                        )}
                                    </td>
                                    <td>
                                        <input 
                                            type="date" 
                                            value={item.warranty_issuance_date || ''} 
                                            onChange={e => onItemChange(index, 'warranty_issuance_date', e.target.value)} 
                                            disabled={disabled || !isWarrantyIssued} 
                                            data-no-scan="true" 
                                            style={{ minWidth: '100px', width: '100%', padding: '4px 6px', border: '1px solid #e5e7eb', borderRadius: '4px', background: isWarrantyIssued ? '#fff' : '#f3f4f6', fontSize: '11px' }} 
                                        />
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="number" value={item.price} onChange={e => onItemChange(index, 'price', e.target.value)} step="1" min="0" disabled={disabled} data-no-scan="true" className={errors[`price_${index}`] ? 'ozui-error' : ''} placeholder="0" dir="ltr" style={{ minWidth: '50px', width: '100%', padding: '4px 6px', fontSize: '12px', outline: 'none', backgroundColor: '#fff', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: errors[`price_${index}`] ? '1px solid #dc2626' : '1px solid #e5e7eb' }} />
                                            {errors[`price_${index}`] && <div className="ozui-table-error" style={{ fontSize: '10px' }}>{errors[`price_${index}`]}</div>}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="number" value={item.quantity} onChange={e => onItemChange(index, 'quantity', e.target.value)} step="1" min="1" max={item.available_stock} disabled={disabled} data-no-scan="true" className={errors[`quantity_${index}`] ? 'ozui-error' : ''} placeholder="0" dir="ltr" style={{ minWidth: '45px', width: '100%', padding: '4px 6px', fontSize: '12px', outline: 'none', backgroundColor: '#fff', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: errors[`quantity_${index}`] ? '1px solid #dc2626' : '1px solid #e5e7eb' }} />
                                            {errors[`quantity_${index}`] && <div className="ozui-table-error" style={{ fontSize: '10px' }}>{errors[`quantity_${index}`]}</div>}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <input type="text" value={item.total || Math.round((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0))} readOnly data-no-scan="true" style={{ minWidth: '60px', width: '100%', padding: '4px 6px', fontSize: '12px', fontWeight: '600', outline: 'none', backgroundColor: '#f3f4f6', color: '#1f2937', boxSizing: 'border-box', borderRadius: '4px', border: '1px solid #e5e7eb', cursor: 'default' }} />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            <span className={`ozui-stock-badge ${isLowStock ? 'ozui-low-stock' : ''}`} style={{ fontSize: '11px', minWidth: '45px', display: 'inline-block', textAlign: 'center', padding: '2px 6px', borderRadius: '4px', backgroundColor: isLowStock ? '#fef2f2' : '#f0fdf4', color: isLowStock ? '#dc2626' : '#059669', fontWeight: '600', width: '100%' }}>
                                                {Math.round(parseFloat(item.available_stock || 0))}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-table-field">
                                            {isCentralized ? (
                                                <span style={{
                                                    fontSize: '9px',
                                                    background: isOwner ? '#10b981' : '#6366f1',
                                                    color: 'white',
                                                    padding: '1px 5px',
                                                    borderRadius: '8px',
                                                    display: 'inline-block',
                                                    fontWeight: '600',
                                                    whiteSpace: 'nowrap',
                                                    width: '100%',
                                                    textAlign: 'center'
                                                }}>
                                                    {isOwner ? '⭐' : '🌐'}
                                                </span>
                                            ) : (
                                                <span style={{
                                                    fontSize: '9px',
                                                    background: '#e5e7eb',
                                                    color: '#6b7280',
                                                    padding: '1px 5px',
                                                    borderRadius: '8px',
                                                    display: 'inline-block',
                                                    fontWeight: '600',
                                                    whiteSpace: 'nowrap',
                                                    width: '100%',
                                                    textAlign: 'center'
                                                }}>
                                                    📍
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <button type="button" className="ozui-table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || items.length === 1} title={translations?.remove || 'Remove'} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px 6px', borderRadius: '4px', transition: 'background-color 0.15s ease', fontSize: '12px' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==============================================
// CREATE SALE SESSION ID
// ==============================================
const createSaleSessionId = () => {
    if (globalThis.crypto?.randomUUID) {
        return globalThis.crypto.randomUUID().replace(/-/g, '');
    }
    return (`sale_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`)
        .slice(0, 32);
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const SolarSpecialSell = ({ saleId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const [formSessionId, setFormSessionId] = useState(createSaleSessionId);

    const initialFormData = {
        date: new Date().toISOString().split('T')[0], bn: '',
        customer_name: '', customer_phone: '', customer_address: '', customer_id: null,
        subtotal: '0', tax: '0', discount: '0', grand_total: '0',
        paid_amount: '0', balance: '0', payment_method: 'cash', payment_status: 'unpaid',
        notes: '', items: [], packing_status: 'unpacked'
    };

    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [currentThemeColor, setCurrentThemeColor] = useState(() => localStorage.getItem('ourzest_theme') || '#6366f1');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });

    const salePayment = useSalePayments({
        apiUrl: `${API_BASE}/mechanics_account.php`,
        shopId: currentShop?.shop_id,
        username: user?.username,
        customerId: isEditMode ? null : formData.customer_id,
        invoiceTotal: Number(formData.grand_total) || 0
    });

    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);

    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [searchingCustomer, setSearchingCustomer] = useState(false);

    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [searchingProducts, setSearchingProducts] = useState(false);

    const [isScannerActive, setIsScannerActive] = useState(true);
    const [lastScannedBarcode, setLastScannedBarcode] = useState('');
    const barcodeInputRef = useRef(null);

    const [businessInfo, setBusinessInfo] = useState({
        businessname: '', businessaddress: '', phonenumber: '', email: '', bussinesslogo: '', bussinesslogo_url: ''
    });

    const printWindowRef = useRef(null);
    const isPrintingRef = useRef(false);

    const customerNameRef = useRef(null);
    const toastTimerRef = useRef(null);
    const customerSearchRef = useRef(null);
    const productSearchRef = useRef(null);
    const isSavingRef = useRef(false);
    const submitButtonRef = useRef(null);
    const draftsDropdownRef = useRef(null);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            if (printWindowRef.current && !printWindowRef.current.closed) {
                printWindowRef.current.close();
            }
        };
    }, []);

    // ==============================================
    // FETCH PRODUCTS
    // ==============================================
    const fetchProductsBySearch = useCallback(async (term) => {
        if (!term || term.length < 2 || !user?.username || !currentShop?.shop_id) return [];
        try {
            const response = await fetch(PRODUCT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_products',
                    searchTerm: term,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    language: currentLanguage
                })
            });
            const data = await parseJsonResponse(response, 'Products API');
            if (data.status === 'success') {
                const rows = data.data || [];
                return rows.map(p => ({
                    id: p.id,
                    product_name: p.product_name,
                    product_code: p.product_code || '',
                    batch_no: p.batch_no || '',
                    barcode: p.barcode || '',
                    warranty: p.warranty || '',
                    sell_price: parseFloat(p.sell_price) || 0,
                    sell_price_per_unit: parseFloat(p.sell_price_per_unit) || 0,
                    sell_per_pack: p.sell_per_pack ? parseFloat(p.sell_per_pack) : null,
                    quantity: parseFloat(p.quantity) || 0,
                    bquantity: p.bquantity || '',
                    quantity_alert: parseFloat(p.quantity_alert) || 5,
                    category: p.category || '',
                    items_per_pack: parseFloat(p.items_per_pack) || 1,
                    packing_type: p.packing_type || null,
                    sub_packing_type: p.sub_packing_type || null,
                    packaging_info: p.packaging_info || null,
                    unit_of_measure: p.unit_of_measure || 'piece',
                    has_packaging: p.has_packaging || false,
                    sell_method: p.sell_method || 'sub_packing',
                    is_product_centralized: p.is_product_centralized || 0,
                    centralized: p.centralized || '',
                    centralized_shops: p.centralized_shops || [],
                    owner_shop_id: p.owner_shop_id || null,
                    supplier_name: p.supplier_name || '',
                    company: p.company || '',
                    original_language: p.original_language || 'en',
                    _original: p._original || {},
                    _translation: p._translation || null,
                    gunit: p.gunit || p.unit_of_measure || '',
                    size: p.size || '',
                    gauge: p.gauge || '',
                    weight: p.weight || '',
                    class: p.class || ''
                }));
            }
            return [];
        } catch (error) {
            console.error('Product search error:', error);
            return [];
        }
    }, [user?.username, currentShop?.shop_id, currentLanguage]);

    // ==============================================
    // ADD PRODUCT TO ITEMS
    // ==============================================
    const addProductToItems = useCallback((product) => {
        if (!product || !product.id) return;

        const lockKey = `scan_lock_${product.id}`;
        if (window[lockKey]) {
            console.warn("Duplicate scan prevented for product:", product.product_name);
            return;
        }

        window[lockKey] = true;
        setTimeout(() => {
            window[lockKey] = false;
        }, 1500);

        setFormData(prev => {
            const existingIndex = prev.items.findIndex(item => item.product_id === product.id);
            const availableStock = computeAvailableStock(product);

            let newQty = 1;
            let updatedItems = [...prev.items];

            if (existingIndex >= 0) {
                const existing = updatedItems[existingIndex];
                newQty = (parseFloat(existing.quantity) || 0) + 1;

                if (availableStock > 0 && newQty > availableStock) {
                    showToast('error', 'Cannot add more than available stock');
                    return prev;
                }

                updatedItems[existingIndex] = {
                    ...existing,
                    quantity: newQty,
                    available_stock: availableStock,
                    total: String(Math.round((parseFloat(existing.price) || 0) * newQty)),
                    is_centralized: product.is_product_centralized || 0,
                    centralized: product.centralized || '',
                    owner_shop_id: product.owner_shop_id || null,
                    is_owner_shop: product.owner_shop_id == currentShop?.shop_id,
                    supplier_name: product.supplier_name || '',
                    company: product.company || '',
                    gunit: product.gunit || product.unit_of_measure || '',
                    size: product.size || '',
                    gauge: product.gauge || '',
                    weight: product.weight || '',
                    class: product.class || ''
                };
            } else {
                const newItem = buildSaleItem(product, currentShop?.shop_id);
                updatedItems = [...prev.items, newItem];
            }

            return { ...prev, items: updatedItems };
        });

        setProductSearchTerm('');
        setShowProductDropdown(false);
    }, [showToast, currentShop?.shop_id]);

    // ==============================================
    // HANDLE BARCODE SCAN
    // ==============================================
    const handleBarcodeScan = useCallback(async (barcode) => {
        if (!barcode || barcode.trim() === '') return;
        if (!user?.username || !currentShop?.shop_id) {
            showToast('error', 'No shop selected');
            return;
        }
        setLastScannedBarcode(barcode);
        try {
            const results = await fetchProductsBySearch(barcode);
            if (results && results.length > 0) {
                const exactMatch = results.find(p => p.barcode === barcode || p.product_code === barcode);
                const product = exactMatch || results[0];
                addProductToItems(product);
                showToast('success', `${product.product_name} - ${translations?.productFound || 'Product found!'}`);
            } else {
                showToast('warning', `${translations?.productNotFound || 'Product not found'}: ${barcode}`);
            }
        } catch (error) {
            console.error('Barcode scan error:', error);
            showToast('error', translations?.networkError || 'Error processing barcode scan');
        }
    }, [user?.username, currentShop?.shop_id, translations, fetchProductsBySearch, addProductToItems, showToast]);

    useBarcodeScanner(handleBarcodeScan, isScannerActive);

    // ==============================================
    // DRAFT FUNCTIONS
    // ==============================================
    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `sell_draft_${userId}_${shopId}`;
    };

    const saveDraft = () => {
        const hasData = (Array.isArray(formData.items) && formData.items.length > 0) || formData.customer_name?.trim() || formData.customer_phone?.trim();
        if (!hasData) {
            showToast('warning', 'Cannot save empty draft');
            return;
        }
        const draftId = Date.now();
        const draft = { id: draftId, ...formData, savedAt: new Date().toISOString(), shop_id: currentShop?.shop_id };
        const existingDrafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(limitedDrafts));
        showToast('success', 'Draft saved successfully!');
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    const loadDraft = (draft) => {
        setFormData({ ...initialFormData, ...draft, date: draft.date || new Date().toISOString().split('T')[0], items: draft.items || [] });
        setShowDraftsList(false);
        showToast('success', translations.draftLoaded || 'Draft loaded successfully!');
        setTimeout(() => { if (customerNameRef.current) customerNameRef.current.focus(); }, 100);
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', 'Draft deleted successfully!');
    };

    const clearDraft = () => {
        setFormData(initialFormData);
        salePayment.resetPayments();
        setProductSearchTerm('');
        setErrors({});
        setLastScannedBarcode('');
        showToast('success', translations.draftCleared || 'Form cleared');
        setTimeout(() => { if (customerNameRef.current) customerNameRef.current.focus(); }, 100);
    };

    // ==============================================
    // THEME AND DARK MODE
    // ==============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
        document.documentElement.style.setProperty('--ozui-primary', color);
        document.documentElement.style.setProperty('--ozui-primary-dark', color);
        document.documentElement.style.setProperty('--ozui-primary-light', color);
        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else { r = 99; g = 102; b = 241; }
        let style = document.getElementById('dynamic-orb-styles-sell');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-orb-styles-sell';
            document.head.appendChild(style);
        }
        style.textContent = `
        .ozui-header-bg-animation .gradient-orb.orb-1 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%); }
        .ozui-header-bg-animation .gradient-orb.orb-2 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%); }
        .ozui-header-bg-animation .gradient-orb.orb-3 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%); }
        .ozui-header-bg-animation .gradient-orb.orb-4 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%); }
        .ozui-header-bg-animation .gradient-orb.orb-5 { background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%); }
        `;
        setCurrentThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        const handleStorageChange = (e) => { if (e.key === 'ourzest_theme') applyTheme(e.newValue || '#6366f1'); };
        const handleThemeChanged = (event) => { if (event.detail && event.detail.theme) applyTheme(event.detail.theme); };
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
        };
    }, [applyTheme]);

    // ==============================================
    // DARK MODE
    // ==============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);
            const container = document.querySelector('.ozui-container');
            if (container) {
                if (isDarkMode) container.classList.add('ozui-dark');
                else container.classList.remove('ozui-dark');
            }
            if (isDarkMode) {
                document.body.classList.add('ozui-dark');
                document.documentElement.classList.add('ozui-dark');
            } else {
                document.body.classList.remove('ozui-dark');
                document.documentElement.classList.remove('ozui-dark');
            }
        };
        syncDarkMode();
        const handleStorageChange = (e) => { if (e.key === 'ourzest_darkMode') syncDarkMode(); };
        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozui-container');
                if (container) {
                    if (isDark) container.classList.add('ozui-dark');
                    else container.classList.remove('ozui-dark');
                }
            } else syncDarkMode();
        };
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        };
    }, []);

    // ==============================================
    // LANGUAGE (Static - no API)
    // ==============================================
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                setTranslations(sellTranslations[lang] || sellTranslations.en);
            }
        };
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    setTranslations(sellTranslations[newLang] || sellTranslations.en);
                }
            }
        };
        setTranslations(sellTranslations[currentLanguage] || sellTranslations.en);
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
        };
    }, [currentLanguage]);

    // ==============================================
    // RTL - Set direction but keep numbers English
    // ==============================================
    useEffect(() => {
        const isRTL = RTL_LANGUAGE_CODES.has(currentLanguage);
        const container = document.querySelector('.ozui-container');
        if (container) {
            if (isRTL) { container.classList.add('ozui-rtl'); container.classList.remove('ozui-ltr'); }
            else { container.classList.add('ozui-ltr'); container.classList.remove('ozui-rtl'); }
        }
        if (isRTL) { 
            document.documentElement.dir = 'rtl'; 
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // ==============================================
    // DRAFTS LOAD
    // ==============================================
    useEffect(() => { loadDrafts(); }, [currentShop]);

    // ==============================================
    // CLICK OUTSIDE HANDLERS
    // ==============================================
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(event.target)) setShowDraftsList(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customerSearchRef.current && !customerSearchRef.current.contains(event.target)) setShowCustomerDropdown(false);
            if (productSearchRef.current && !productSearchRef.current.contains(event.target)) setShowProductDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ==============================================
    // TAB KEY HANDLER
    // ==============================================
    useEffect(() => {
        const handleTabKey = (e) => {
            if (e.key === 'Tab') {
                setShowCustomerDropdown(false);
                setShowProductDropdown(false);
                setShowDraftsList(false);
            }
        };
        document.addEventListener('keydown', handleTabKey);
        return () => document.removeEventListener('keydown', handleTabKey);
    }, []);

    // ==============================================
    // AUTO FOCUS
    // ==============================================
    useEffect(() => {
        setTimeout(() => {
            if (customerNameRef.current) {
                customerNameRef.current.focus();
            }
        }, 100);
    }, []);

    // ==============================================
    // FETCH BUSINESS INFO
    // ==============================================
    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=' + encodeURIComponent(user.username), {
                method: 'GET', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, credentials: 'include'
            });
            const data = await parseJsonResponse(response, 'Business info API');
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '', businessaddress: data.businessaddress || '',
                    phonenumber: data.phonenumber || '', email: data.email || '',
                    bussinesslogo: data.bussinesslogo || '', bussinesslogo_url: data.bussinesslogo_url || ''
                });
            } else {
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || '', businessaddress: user.business_address || '',
                    phonenumber: user.phonenumber || '', email: user.email || '', bussinesslogo: '', bussinesslogo_url: ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            setBusinessInfo({
                businessname: user.business_name || user.fullname || '', businessaddress: user.business_address || '',
                phonenumber: user.phonenumber || '', email: user.email || '', bussinesslogo: '', bussinesslogo_url: ''
            });
        }
    }, [user]);

    useEffect(() => {
        if (currentShop?.shop_id) {
            fetchBusinessInfo();
        }
    }, [currentShop?.shop_id, fetchBusinessInfo]);

    // ==============================================
    // LOAD SALE DATA FOR EDIT
    // ==============================================
    useEffect(() => {
        if (saleId) {
            setIsEditMode(true);
            loadSaleData(saleId);
        }
    }, [saleId]);

    // ==============================================
    // CALCULATE TOTALS
    // ==============================================
    useEffect(() => { calculateTotals(); }, [formData.items, formData.tax, formData.discount, formData.paid_amount]);

    // ==============================================
    // LOAD SALE DATA
    // ==============================================
    const loadSaleData = async (id) => {
        if (!user?.username || !currentShop?.shop_id) return;
        setLoading(true);
        try {
            const response = await fetch(SELL_API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id: id, username: user.username, shop_id: currentShop.shop_id, language: currentLanguage })
            });
            const data = await parseJsonResponse(response, 'Sales API');
            if (data.status === 'success' && data.data) {
                const sale = data.data;
                setFormData(prev => ({
                    ...prev, date: sale.date || new Date().toISOString().split('T')[0],
                    bn: sale.bn || '',
                    customer_id: sale.customer_id || null, customer_name: sourceValue(sale, 'customer_name'),
                    customer_phone: sale.customer_phone || '', customer_address: sourceValue(sale, 'customer_address'),
                    subtotal: sale.subtotal || '0', tax: sale.tax || '0', discount: sale.discount || '0',
                    grand_total: sale.grand_total || '0', paid_amount: sale.paid_amount || '0',
                    balance: sale.balance || '0', payment_method: sale.payment_method || 'cash',
                    payment_status: sale.payment_status || 'unpaid', notes: sourceValue(sale, 'notes'),
                    original_language: sale.original_language || currentLanguage,
                    items: (sale.items || []).map(item => ({
                        product_id: item.product_id || '', product_name: sourceValue(item, 'product_name'),
                        product_code: item.product_code || '', batch_no: item.batch_no || '',
                        barcode: item.barcode || '', warranty: item.warranty || '',
                        product_warranty: item.product_warranty || item.warranty || '',
                        WarrantyStatus: Number(item.WarrantyStatus ?? item.warranty_status ?? (item.warranty ? 1 : 0)) === 1 ? 1 : 0,
                        issue_warranty: Number(item.WarrantyStatus ?? item.warranty_status ?? (item.warranty ? 1 : 0)) === 1,
                        warranty_issuance_date: item.warranty_issuance_date || '', price: item.price || 0,
                        quantity: item.quantity || item.display_quantity || '1',
                        available_stock: item.available_stock || 0, quantity_alert: item.quantity_alert || 5,
                        total: item.total || String(Math.round((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0))),
                        is_centralized: item.is_centralized || 0,
                        owner_shop_id: item.owner_shop_id || null,
                        is_owner_shop: item.owner_shop_id == currentShop?.shop_id,
                        supplier_name: sourceValue(item, 'supplier_name'),
                        company: sourceValue(item, 'company'),
                        original_language: item.original_language || sale.original_language || currentLanguage,
                        _original: item._original || {},
                        gunit: sourceValue(item, 'gunit') || sourceValue(item, 'unit_of_measure') || '',
                        size: sourceValue(item, 'size') || '',
                        gauge: sourceValue(item, 'gauge') || '',
                        weight: sourceValue(item, 'weight') || '',
                        class: sourceValue(item, 'class') || ''
                    })),
                    packing_status: sale.packing_status || 'unpacked'
                }));
            } else {
                showToast('error', data.message || 'Failed to load sale data');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // SEARCH CUSTOMERS
    // ==============================================
    const searchCustomers = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2 || !currentShop?.shop_id) {
            setCustomerSearchResults([]);
            setShowCustomerDropdown(false);
            return;
        }
        setSearchingCustomer(true);
        try {
            const response = await fetch(CUSTOMER_API, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_records', shop_id: currentShop.shop_id, language: currentLanguage })
            });
            const data = await parseJsonResponse(response, 'Customers API');
            if (data.status === 'success' && data.data) {
                const filtered = data.data.filter(customer =>
                    customer.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    customer.phone?.toLowerCase().includes(searchTerm.toLowerCase())
                );
                setCustomerSearchResults(filtered.slice(0, 20));
                setShowCustomerDropdown(filtered.length > 0);
            } else {
                setCustomerSearchResults([]);
                setShowCustomerDropdown(false);
            }
        } catch (error) {
            console.error('Error searching customers:', error);
            setCustomerSearchResults([]);
            setShowCustomerDropdown(false);
        } finally {
            setSearchingCustomer(false);
        }
    }, [currentShop?.shop_id, currentLanguage]);

    // ==============================================
    // SEARCH PRODUCTS
    // ==============================================
    const searchProducts = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setFilteredProducts([]);
            setShowProductDropdown(false);
            return;
        }
        setSearchingProducts(true);
        const products = await fetchProductsBySearch(searchTerm);
        setFilteredProducts(products);
        setShowProductDropdown(products.length > 0);
        setSearchingProducts(false);
    }, [fetchProductsBySearch]);

    // ==============================================
    // SELECT CUSTOMER
    // ==============================================
    const selectCustomer = (customer) => {
        setFormData(prev => ({
            ...prev, customer_id: customer.customerid, customer_name: customer.customer_name,
            customer_phone: customer.phone || '', customer_address: customer.address || '',
            _original_customer: customer._original || null
        }));
        setShowCustomerDropdown(false);
        setCustomerSearchResults([]);
        showToast('success', `${translations?.customerSelected || 'Customer selected'}: ${customer.customer_name}`);
        setTimeout(() => {
            const productSearchInput = productSearchRef.current?.querySelector('input');
            if (productSearchInput) productSearchInput.focus();
        }, 100);
    };

    // ==============================================
    // HANDLE PRODUCT SELECT
    // ==============================================
    const handleProductSelect = (product) => {
        addProductToItems(product);
    };

    // ==============================================
    // HANDLE ROW PRODUCT SELECT
    // ==============================================
    const handleRowProductSelect = (index, product) => {
        const newItem = buildSaleItem(product, currentShop?.shop_id);
        setFormData(prev => {
            const items = [...prev.items];
            items[index] = {
                ...items[index],
                ...newItem,
                quantity: items[index].quantity || '1'
            };
            return { ...prev, items };
        });
        const errKey1 = `product_${index}`;
        const errKey2 = `price_${index}`;
        const errKey3 = `quantity_${index}`;
        setErrors(prev => {
            const e = { ...prev };
            delete e[errKey1];
            delete e[errKey2];
            delete e[errKey3];
            return e;
        });
    };

    // ==============================================
    // HANDLE ITEM CHANGE
    // ==============================================
    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        const item = { ...updatedItems[index] };
        if (field === 'price') {
            item.price = value;
            item.total = String(Math.round((parseFloat(value) || 0) * (parseFloat(item.quantity) || 0)));
        } else if (field === 'quantity') {
            const qty = parseFloat(value) || 0;
            const maxQty = parseFloat(item.available_stock) || 0;
            if (maxQty > 0 && qty > maxQty) {
                setErrors(prev => ({ ...prev, [`quantity_${index}`]: translations?.quantityExceedsStock || 'Quantity exceeds available stock' }));
            } else {
                setErrors(prev => { const e = { ...prev }; delete e[`quantity_${index}`]; return e; });
            }
            item.quantity = value;
            item.total = String(Math.round((parseFloat(item.price) || 0) * qty));
        } else if (field === 'issue_warranty') {
            const availableWarranty = String(item.product_warranty || item.warranty || '').trim();
            item.issue_warranty = Boolean(value && availableWarranty);
            item.WarrantyStatus = item.issue_warranty ? 1 : 0;
            item.warranty_issuance_date = item.issue_warranty
                ? (item.warranty_issuance_date || formData.date || new Date().toISOString().split('T')[0])
                : '';
        } else {
            item[field] = value;
        }
        updatedItems[index] = item;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    // ==============================================
    // ADD / REMOVE ITEM
    // ==============================================
    const addItem = () => {
        setFormData(prev => ({
            ...prev, items: [...prev.items, {
                product_id: '', product_name: '', product_code: '', batch_no: '', barcode: '',
                warranty: '', product_warranty: '', issue_warranty: false, WarrantyStatus: 0,
                warranty_issuance_date: '',
                price: '', quantity: '1', available_stock: 0, quantity_alert: 5, total: '0',
                is_centralized: 0, owner_shop_id: null, is_owner_shop: false,
                supplier_name: '', company: '',
                gunit: '', size: '', gauge: '', weight: '', class: ''
            }]
        }));
    };

    const removeItem = (index) => {
        if (Array.isArray(formData.items) && formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({ ...prev, items: updatedItems }));
        } else {
            setFormData(prev => ({ ...prev, items: [] }));
        }
    };

    // ==============================================
    // CALCULATE TOTALS
    // ==============================================
    const calculateTotals = () => {
        let subtotal = 0;
        formData.items.forEach(item => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            subtotal += price * quantity;
        });
        const tax = parseFloat(formData.tax) || 0;
        const discount = parseFloat(formData.discount) || 0;
        const grandTotal = subtotal + tax - discount;
        const paid = parseFloat(formData.paid_amount) || 0;
        const balance = grandTotal - paid;
        const paymentStatus = balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid');
        setFormData(prev => ({
            ...prev, subtotal: String(Math.round(subtotal)), grand_total: String(Math.round(grandTotal)),
            balance: String(Math.round(balance)), payment_status: paymentStatus
        }));
    };

    // ==============================================
    // VALIDATE FORM
    // ==============================================
    const validateForm = () => {
        const newErrors = {};

        const bn = String(formData.bn || '').trim();
        if (!bn) {
            newErrors.bn = 'BN is required';
        } else if (!/^[A-Za-z0-9]+$/.test(bn)) {
            newErrors.bn = 'BN may contain letters and numbers only';
        }

        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                if (!item.product_id && !item.product_name?.trim()) {
                    newErrors[`product_${index}`] = translations?.productRequired || 'Please select a product';
                }
                const qty = parseFloat(item.quantity) || 0;
                const maxQty = parseFloat(item.available_stock) || 0;
                if (maxQty > 0 && qty > maxQty) {
                    newErrors[`quantity_${index}`] = translations?.quantityExceedsStock || 'Quantity exceeds available stock';
                }
                if (qty <= 0) {
                    newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Please enter a valid quantity';
                }
                const price = parseFloat(item.price) || 0;
                if (price <= 0) {
                    newErrors[`price_${index}`] = translations?.invalidPrice || 'Please enter a valid price';
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ==============================================
    // HANDLE CHANGE
    // ==============================================
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'customer_name' ? { customer_id: null } : {})
        }));
        if (name === 'customer_name') searchCustomers(value);
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleBnChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, bn: value }));
        if (errors.bn) setErrors(prev => ({ ...prev, bn: '' }));
    };

    const handlePaidAmountChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, paid_amount: value }));
        const paid = parseFloat(value) || 0;
        const grandTotal = parseFloat(formData.grand_total) || 0;
        const balance = grandTotal - paid;
        setFormData(prev => ({
            ...prev, balance: String(Math.round(balance)),
            payment_status: balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid')
        }));
    };

    // ==============================================
    // ESCAPE HTML FOR RECEIPT
    // ==============================================
    const escapeHtml = (text) => {
        if (!text) return '';
        return String(text).replace(/[&<>]/g, function (m) {
            if (m === '&') return '&amp;';
            if (m === '<') return '&lt;';
            if (m === '>') return '&gt;';
            return m;
        });
    };

    const splitBusinessName = (name = '') => {
        const words = String(name).trim().split(/\s+/).filter(Boolean);
        const secondaryWords = words.slice(2);
        return {
            primary: words.slice(0, 2).join(' '),
            secondary: secondaryWords.join(' '),
            secondaryUnits: secondaryWords.length === 1 ? Array.from(secondaryWords[0]) : secondaryWords
        };
    };

    const formatReceiptCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0';
        return `Rs. ${Math.round(parseFloat(amount) || 0)}`;
    };

    const formatReceiptDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const timeStr = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
        return `${dateStr}  ${timeStr}`;
    };

    // ==============================================
    // HANDLE DIRECT PRINT
    // ==============================================
    const handleDirectPrint = async (receiptData = formData) => {
        if (isPrintingRef.current) { showToast('warning', 'Print already in progress...'); return; }
        if (!receiptData.items?.length) { showToast('warning', 'No items to print receipt'); return; }
        if (printWindowRef.current && !printWindowRef.current.closed) { printWindowRef.current.focus(); return; }

        isPrintingRef.current = true;
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            isPrintingRef.current = false;
            showToast('error', 'Please allow popups for printing');
            return;
        }
        printWindowRef.current = printWindow;

        const businessName = businessInfo?.businessname || user?.business_name || user?.fullname || currentShop?.shop_name || 'OurZest Store';
        const businessLogo = businessInfo?.bussinesslogo_url || businessInfo?.bussinesslogo || '';
        const businessAddress = businessInfo?.businessaddress || user?.business_address || '';
        const businessPhone = businessInfo?.phonenumber || user?.phonenumber || '';
        const directBusinessName = splitBusinessName(businessName);
        const customerName = receiptData.customer_name || 'Walk-in Customer';
        const customerPhone = receiptData.customer_phone || '';
        const saleReference = receiptData.sell_id || receiptData.id || '';
        const allowedSizes = { A4: 'A4', A5: 'A5', LETTER: 'Letter', LEGAL: 'Legal' };
        const savedSize = String(localStorage.getItem('receipt_paper_size') || 'A4').toUpperCase();
        const printPageSize = allowedSizes[savedSize] || 'A4';
        const receiptDateTime = formatReceiptDateTime(new Date().toISOString());

        printWindow.document.write(`<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Sales Receipt${saleReference ? ' #' + escapeHtml(saleReference) : ''}</title>
<style>
@page { size: ${printPageSize} portrait; margin: 10mm; }
* { box-sizing: border-box; }
:root { --ink: #000; --muted: #444; --line: #b8b8b8; --header-mark-size: 100px; }
body { margin: 0; color: var(--ink); background: #fff; font: 9pt/1.4 'Segoe UI', Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.receipt { width: 100%; margin: 0 auto; }
.header { margin-bottom: 0; text-align: left; }
.brand { 
    display: flex; 
    align-items: center; 
    justify-content: flex-start; 
    gap: 14px; 
    min-height: 120px; 
    padding: 10px 2px 16px; 
}
.logo { 
    display: block; 
    flex: 0 0 var(--header-mark-size); 
    width: var(--header-mark-size); 
    height: var(--header-mark-size); 
    max-width: var(--header-mark-size); 
    max-height: var(--header-mark-size); 
    object-fit: contain; 
    filter: grayscale(100%) contrast(115%); 
}
.business-copy { 
    min-width: 0; 
    text-align: left; 
    flex: 1;
}
.business-name-lockup { 
    display: inline-flex; 
    flex-direction: column; 
    width: max-content; 
    max-width: 100%; 
}
.business-name-primary { 
    font-size: 24pt; 
    font-weight: 850; 
    letter-spacing: .25px; 
    line-height: 1.02; 
    overflow-wrap: anywhere; 
}
.business-name-secondary { 
    display: flex; 
    justify-content: flex-start; 
    width: 100%; 
    margin-top: 3px; 
    font-size: 14pt; 
    font-weight: 650; 
    line-height: 1.1; 
    overflow-wrap: anywhere; 
    gap: 5px;
}
.business-detail { 
    margin-top: 4px; 
    color: var(--muted); 
    font-size: 9pt; 
    line-height: 1.3; 
    overflow-wrap: anywhere; 
}
.contact-line { 
    display: flex; 
    justify-content: flex-start; 
    flex-wrap: wrap; 
    gap: 2px 14px; 
}
.receipt-heading { 
    padding: 6px 14px 7px; 
    color: #000; 
    background: #f7f7f7; 
    border: 1px solid #888; 
    border-radius: 3px 3px 0 0; 
}
.receipt-title { 
    font-size: 13pt; 
    font-weight: 800; 
    letter-spacing: 2px; 
    text-transform: uppercase; 
}
.meta { 
    display: grid; 
    grid-template-columns: 1.25fr .7fr .7fr 1fr; 
    gap: 0; 
    margin: 0 0 10px; 
    background: #fff; 
    border: 1px solid #888; 
    border-top: 0; 
    border-radius: 0 0 3px 3px; 
    overflow: hidden; 
}
.meta > span { 
    min-height: 38px; 
    padding: 5px 8px; 
    background: #fff; 
    border: 0; 
    border-right: 1px solid #aaa; 
    display: flex; 
    flex-direction: column; 
    justify-content: center; 
}
.meta > span:last-child { border-right: 0; }
.meta strong { 
    color: var(--muted); 
    font-size: 6.5pt; 
    letter-spacing: .5px; 
    text-transform: uppercase; 
}
.meta-value { 
    color: var(--ink); 
    font-weight: 650; 
    overflow-wrap: anywhere; 
}
.meta-subvalue { 
    margin-top: 1px; 
    color: var(--muted); 
    font-size: 7pt; 
    overflow-wrap: anywhere; 
}
table { 
    width: 100%; 
    margin: 0 0 12px; 
    border-collapse: separate; 
    border-spacing: 0; 
    border: 1px solid var(--line); 
    border-radius: 3px; 
    overflow: hidden; 
}
thead { display: table-header-group; }
th { 
    color: #000; 
    background: #f7f7f7; 
    font-size: 6.5pt; 
    font-weight: 700; 
    letter-spacing: .3px; 
    line-height: 1.1; 
    text-transform: uppercase; 
}
th, td { 
    padding: 4px 5px; 
    border: 0; 
    border-right: 1px solid var(--line); 
    border-bottom: 1px solid var(--line); 
    line-height: 1.2; 
    vertical-align: middle; 
}
th:last-child, td:last-child { border-right: 0; }
tbody tr:last-child td { border-bottom: 0; }
tbody tr:nth-child(even) { background: #fafafa; }
th:not(:nth-child(2)), td:not(:nth-child(2)) { text-align: center; }
th:nth-child(2), td:nth-child(2) { text-align: left; }
tr { break-inside: avoid; page-break-inside: avoid; }
.product { font-weight: 650; overflow-wrap: anywhere; }
.issued-warranty { margin-top: 2px; color: #15803d; font-size: 6pt; font-weight: 650; }
.payment-section { 
    display: flex; 
    justify-content: flex-end; 
    break-inside: avoid; 
    page-break-inside: avoid; 
}
.payment-box { 
    width: 280px; 
    background: #fff; 
    border: 1px solid var(--line); 
    border-radius: 3px; 
    overflow: hidden; 
}
.payment-row { 
    display: flex; 
    justify-content: space-between; 
    gap: 16px; 
    padding: 4px 10px; 
}
.payment-row:first-child { padding-top: 7px; }
.payment-row:last-child { padding-bottom: 7px; }
.payment-row.total { 
    margin: 3px 0; 
    padding: 6px 10px; 
    color: #000; 
    background: #f7f7f7; 
    border-top: 1px solid #999; 
    border-bottom: 1px solid #999; 
    font-size: 11pt; 
    font-weight: 800; 
}
.footer { 
    margin-top: 16px; 
    padding-top: 10px; 
    border-top: 1px solid var(--line); 
    color: var(--muted); 
    font-size: 7pt; 
    text-align: center; 
    break-inside: avoid; 
}
.thanks { 
    margin-bottom: 2px; 
    color: var(--ink); 
    font-size: 10pt; 
    font-weight: 700; 
}
@media print and (max-width: 160mm) {
  :root { --header-mark-size: 80px; }
  body { font-size: 7.5pt; } 
  .business-name-primary { font-size: 18pt; } 
  .business-name-secondary { font-size: 11pt; } 
  .receipt-title { font-size: 10pt; }
  .brand { min-height: 90px; padding: 6px 2px 10px; } 
  th, td { padding: 3px 4px; font-size: 6pt; }
  .payment-box { width: 220px; } 
  .meta > span { min-height: 32px; padding: 4px 5px; }
  .meta { grid-template-columns: 1.15fr .65fr .65fr 1fr; gap: 0; } 
  .meta strong { font-size: 5.5pt; } 
  .meta-value { font-size: 7pt; } 
  .meta-subvalue { font-size: 6pt; }
}
</style></head><body><main class="receipt">
<header class="header">
  <div class="brand">
    ${businessLogo ? `<img class="logo" src="${escapeHtml(businessLogo)}" alt="${escapeHtml(businessName)}" onerror="this.style.display='none'">` : ''}
    <div class="business-copy">
      <div class="business-name-lockup">
        <div class="business-name-primary">${escapeHtml(directBusinessName.primary)}</div>
        ${directBusinessName.secondary ? `<div class="business-name-secondary">${directBusinessName.secondaryUnits.map(unit => `<span>${escapeHtml(unit)}</span>`).join('')}</div>` : ''}
      </div>
      <div class="contact-line">
        ${businessAddress ? `<div class="business-detail">Address: ${escapeHtml(businessAddress)}</div>` : ''}
        ${businessPhone ? `<div class="business-detail">Phone: ${escapeHtml(businessPhone)}</div>` : ''}
      </div>
    </div>
  </div>
  <div class="receipt-heading"><div class="receipt-title">${translations.salesReceipt || translations.receipt || 'Sales Receipt'}</div></div>
</header>
<section class="meta">
  <span><strong>${translations.customer || 'Customer'}</strong><span class="meta-value">${escapeHtml(customerName)}</span>${customerPhone ? `<small class="meta-subvalue">${translations.customerPhone || 'Phone'}: ${escapeHtml(customerPhone)}</small>` : ''}</span>
  <span><strong>${translations.bn || 'BN'}</strong><span class="meta-value">${escapeHtml(receiptData.bn || '-')}</span></span>
  <span><strong>${translations.dateTime || 'Date & Time'}</strong><span class="meta-value">${escapeHtml(receiptDateTime)}</span></span>
</section>
<table><thead><tr>
  <th style="width:3%">#</th>
  <th style="width:18%">${translations.productName || 'Product'}</th>
  <th style="width:4%">${translations.unit || 'Unit'}</th>
  <th style="width:4%">${translations.size || 'Size'}</th>
  <th style="width:4%">${translations.gauge || 'Gauge'}</th>
  <th style="width:4%">${translations.weight || 'Weight'}</th>
  <th style="width:4%">${translations.class || 'Class'}</th>
  <th style="width:6%">${translations.quantity || 'Qty'}</th>
  <th style="width:8%">${translations.price || 'Price'}</th>
  <th style="width:10%">${translations.total || 'Total'}</th>
</tr></thead><tbody>
${receiptData.items.map((item, index) => `<tr>
  <td>${index + 1}</td>
  <td><div class="product">${escapeHtml(item.product_name || 'Product')}</div>${Number(item.WarrantyStatus) === 1 && item.warranty ? `<div class="issued-warranty">Warranty: ${escapeHtml(item.warranty)}${item.warranty_issuance_date ? ` · ${escapeHtml(item.warranty_issuance_date)}` : ''}</div>` : ''}</td>
  <td>${escapeHtml(item.gunit || item.unit_of_measure || '-')}</td>
  <td>${escapeHtml(item.size || '-')}</td>
  <td>${escapeHtml(item.gauge || '-')}</td>
  <td>${escapeHtml(item.weight || '-')}</td>
  <td>${escapeHtml(item.class || '-')}</td>
  <td>${Math.round(Number(item.quantity) || 0)}</td>
  <td>${formatReceiptCurrency(item.price)}</td>
  <td>${formatReceiptCurrency(item.total ?? Number(item.price) * Number(item.quantity))}</td>
</tr>`).join('')}
</tbody></table>
<section class="payment-section"><div class="payment-box">
  ${Number(receiptData.previous_balance) > 0 ? `<div class="payment-row"><span>${translations.previousBalance || 'Previous Balance'}</span><span>${formatReceiptCurrency(receiptData.previous_balance)}</span></div>` : ''}
  <div class="payment-row"><span>${translations.subtotal || 'Subtotal'}</span><span>${formatReceiptCurrency(receiptData.subtotal)}</span></div>
  ${Number(receiptData.tax) > 0 ? `<div class="payment-row"><span>${translations.tax || 'Tax'}</span><span>${formatReceiptCurrency(receiptData.tax)}</span></div>` : ''}
  ${Number(receiptData.discount) > 0 ? `<div class="payment-row"><span>${translations.discount || 'Discount'}</span><span>-${formatReceiptCurrency(receiptData.discount)}</span></div>` : ''}
  <div class="payment-row total"><span>${translations.grandTotal || translations.total || 'Grand Total'}</span><span>${formatReceiptCurrency(receiptData.payable_total ?? receiptData.grand_total)}</span></div>
  ${Array.isArray(receiptData.payments) ? receiptData.payments.map((payment) => `<div class="payment-row"><span>${escapeHtml(payment.account_name || payment.payment_type)}</span><span>${formatReceiptCurrency(payment.amount)}</span></div>`).join('') : ''}
  ${(Number(receiptData.customer_credit_applied) || 0) > 0 ? `<div class="payment-row"><span>Previous Customer Credit Applied</span><span>${formatReceiptCurrency(receiptData.customer_credit_applied)}</span></div>` : ''}
  <div class="payment-row"><span>${translations.paidAmount || 'Paid'}</span><span>${formatReceiptCurrency(receiptData.paid_amount)}</span></div>
  <div class="payment-row"><span>${translations.balance || 'Balance'}</span><span>${formatReceiptCurrency(receiptData.balance)}</span></div>
</div></section>
<footer class="footer"><div class="thanks">${translations.thankYou || 'Thank you for your purchase!'}</div><div>${translations.poweredBy || 'Powered by OurZest'}</div></footer>
</main><script>
window.onload = async function () {
  await Promise.all(Array.from(document.images).map(img => img.complete ? Promise.resolve() : new Promise(resolve => { img.onload = resolve; img.onerror = resolve; })));
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  window.onafterprint = function () {
    if (window.opener) window.opener.postMessage('printFinished', '*');
  };
  window.print();
};
window.addEventListener('beforeunload', function () { if (window.opener) window.opener.postMessage('printClosed', '*'); });
</script></body></html>`);

        printWindow.document.close();
        printWindow.focus();
        const checkPrintWindow = setInterval(() => {
            if (printWindow.closed) {
                clearInterval(checkPrintWindow);
                isPrintingRef.current = false;
                printWindowRef.current = null;
                setTimeout(() => customerNameRef.current?.focus(), 100);
            }
        }, 500);
    };

    // ==============================================
    // HANDLE SUBMIT
    // ==============================================
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSavingRef.current) {
            showToast('warning', 'Please wait, sale is already being processed...');
            return;
        }
        isSavingRef.current = true;
        if (submitButtonRef.current) submitButtonRef.current.disabled = true;

        if (!currentShop?.shop_id) {
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.');
            isSavingRef.current = false;
            if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            return;
        }
        if (!validateForm()) {
            isSavingRef.current = false;
            if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            return;
        }
        if (!isEditMode) {
            const paymentError = salePayment.validate();
            if (paymentError) {
                showToast('error', paymentError);
                isSavingRef.current = false;
                if (submitButtonRef.current) submitButtonRef.current.disabled = false;
                return;
            }
        }

        setSaving(true);
        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) throw new Error("User not authenticated");
            const action = isEditMode ? 'edit' : 'add';
            const preparedItems = formData.items.map(item => {
                const availableWarranty = String(item.product_warranty || item.warranty || '').trim();
                const issueWarranty = Boolean(item.issue_warranty && availableWarranty);
                return {
                    product_id: item.product_id,
                    product_name: sourceValue(item, 'product_name'),
                    product_code: item.product_code || '',
                    batch_no: item.batch_no || '',
                    barcode: item.barcode || '',
                    warranty: issueWarranty ? availableWarranty : '',
                    warranty_issuance_date: issueWarranty ? (item.warranty_issuance_date || formData.date) : null,
                    WarrantyStatus: issueWarranty ? 1 : 0,
                    quantity: parseFloat(item.quantity) || 0,
                    display_quantity: parseFloat(item.quantity) || 0,
                    price: parseFloat(item.price) || 0,
                    total: (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0),
                    sell_method: item.sell_method || 'sub_packing',
                    packing_type: item.packing_type || null,
                    sub_packing_type: item.sub_packing_type || null,
                    items_per_pack: parseFloat(item.items_per_pack) || 1,
                    is_centralized: item.is_centralized || 0,
                    owner_shop_id: item.owner_shop_id || null,
                    supplier_name: sourceValue(item, 'supplier_name'),
                    company: sourceValue(item, 'company'),
                    original_language: item.original_language || formData.original_language || currentLanguage,
                    gunit: item.gunit || item.unit_of_measure || '',
                    size: item.size || '',
                    gauge: item.gauge || '',
                    weight: item.weight || '',
                    class: item.class || ''
                };
            });

            const submittedGrandTotal = Number(formData.grand_total) || 0;
            const submittedReceivedAmount = isEditMode
                ? (Number(formData.paid_amount) || 0)
                : salePayment.paidTotal;

            const projectedInvoicePaid = isEditMode
                ? Math.min(submittedGrandTotal, submittedReceivedAmount)
                : Math.min(
                    submittedGrandTotal,
                    Math.max(0, submittedReceivedAmount - salePayment.previousBalance) +
                        Math.min(salePayment.creditBalance, submittedGrandTotal)
                );
            const projectedInvoiceBalance = Math.max(0, submittedGrandTotal - projectedInvoicePaid);
            const projectedPaymentStatus = projectedInvoiceBalance <= 0.005
                ? 'paid'
                : (projectedInvoicePaid > 0.005 ? 'partial' : 'unpaid');

            const submittedSale = {
                ...formData,
                bn: String(formData.bn || '').trim(),
                customer_id: formData.customer_id ? Number(formData.customer_id) : null,
                customer_name: String(formData._original_customer?.customer_name ?? formData.customer_name ?? '').trim(),
                customer_address: String(formData._original_customer?.address ?? formData.customer_address ?? '').trim(),
                original_language: formData.original_language || currentLanguage,
                ...(!isEditMode ? salePayment.payload : {}),
                grand_total: submittedGrandTotal,
                paid_amount: submittedReceivedAmount,
                balance: projectedInvoiceBalance,
                payment_status: projectedPaymentStatus,
                items: preparedItems
            };

            const payload = {
                ...submittedSale,
                action,
                username: currentUsername,
                shop_id: currentShop.shop_id,
                language: currentLanguage,
                form_session_id: isEditMode ? null : formSessionId
            };
            if (isEditMode && saleId) payload.id = saleId;

            let response;
            try {
                response = await fetch(SELL_API, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    cache: 'no-store',
                    body: JSON.stringify(payload)
                });
            } catch (networkError) {
                throw new Error(
                    `Could not reach the Sales API. Check the server/CORS connection and deploy the matching PHP file. ` +
                    `(${networkError?.message || 'network request failed'})`
                );
            }
            const data = await parseJsonResponse(response, 'Sales API');

            if (data.status === 'success') {
                const savedSellId = data.data?.sell_id || saleId;
                if (!savedSellId) {
                    throw new Error('The saved sale ID was not returned by the database');
                }

                const savedPaidAmount = Number(data.data?.paid_amount ?? projectedInvoicePaid) || 0;
                const savedBalance = Math.max(
                    0,
                    Number(data.data?.balance ?? projectedInvoiceBalance) || 0
                );
                const savedPaymentStatus = savedBalance <= 0.005
                    ? 'paid'
                    : (savedPaidAmount > 0.005 ? 'partial' : 'unpaid');
                const receiptSnapshot = {
                    ...submittedSale,
                    id: savedSellId,
                    sell_id: savedSellId,
                    shop_id: currentShop.shop_id,
                    customer_name: data.data?.customer?.name || submittedSale.customer_name,
                    paid_amount: savedPaidAmount,
                    balance: savedBalance,
                    payment_status: savedPaymentStatus,
                    customer_credit_applied: Number(data.data?.customer_credit_applied) || 0,
                    items: submittedSale.items
                };

                showToast(
                    'success',
                    data.already_exists
                        ? 'This sale was already recorded; no second copy was created.'
                        : isEditMode
                            ? (translations?.saleUpdated || 'Sale updated successfully!')
                            : (translations?.saleCompleted || 'Sale completed successfully!')
                );

                if (!isEditMode) {
                    setFormSessionId(createSaleSessionId());
                    setFormData({
                        ...initialFormData,
                        date: new Date().toISOString().split('T')[0]
                    });
                    salePayment.resetPayments();
                    setProductSearchTerm('');
                    setErrors({});
                    setTimeout(() => { if (customerNameRef.current) customerNameRef.current.focus(); }, 100);
                }
                if (onSuccess) onSuccess(data.data?.sell_id || saleId);

                setTimeout(() => handleDirectPrint(receiptSnapshot), 300);
            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save sale');
            }
        } catch (error) {
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            isSavingRef.current = false;
            setSaving(false);
            setTimeout(() => {
                if (submitButtonRef.current) submitButtonRef.current.disabled = false;
            }, 2000);
        }
    };

    const isRTL = RTL_LANGUAGE_CODES.has(currentLanguage);

    return (
        <div
            className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}
            dir={isRTL ? 'rtl' : 'ltr'}
            lang={currentLanguage}
        >
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div><div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div><div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode ? (translations?.editSale || 'Edit Sale') : (translations?.newSale || 'New Sale')}</h1>
                            <p>{translations?.sellProducts || 'Sell Products'}</p>
                        </div>
                    </div>
                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button className="ozui-action-btn clear-draft" onClick={clearDraft} disabled={saving} title={translations?.clearForm || 'Clear Form'}>
                                <FontAwesomeIcon icon={faTrashAlt} /><span>{translations?.clear || 'Clear'}</span>
                            </button>
                            <button className="ozui-action-btn save-draft" onClick={saveDraft} disabled={saving} title={translations?.saveDraft || 'Save as Draft'}>
                                <FontAwesomeIcon icon={faCopy} /><span>{translations?.saveDraft || 'Save Draft'}</span>
                            </button>
                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button className="ozui-action-btn drafts-btn" onClick={() => setShowDraftsList(!showDraftsList)} title={translations?.viewDrafts || 'View Drafts'}>
                                    <FontAwesomeIcon icon={faFileAlt} /><span>{translations?.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations?.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations?.noDraftsSaved || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">{draft.bn || draft.customer_name || `Sale Draft #${draft.id}`}</div>
                                                            <div className="draft-details">
                                                                {draft.customer_name && <span>{draft.customer_name}</span>}
                                                                {draft.items && <span>{draft.items.length} items</span>}
                                                            </div>
                                                            <div className="draft-date">{new Date(draft.savedAt).toLocaleDateString()}</div>
                                                        </div>
                                                        <button className="delete-draft-btn" onClick={(e) => { e.stopPropagation(); deleteDraft(draft.id); }}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <button className="ozui-action-btn scanner-toggle" onClick={() => setIsScannerActive(!isScannerActive)} title="Toggle Scanner">
                                <FontAwesomeIcon icon={isScannerActive ? faToggleOn : faToggleOff} />
                                <span>{isScannerActive ? 'Scanner ON' : 'Scanner OFF'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                <input ref={barcodeInputRef} type="text" style={{ position: 'absolute', left: '-9999px', opacity: 0, width: '1px', height: '1px' }} autoComplete="off" tabIndex={-1} onChange={() => { }} data-no-scan="true" />

                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input type="text" id="bn" name="bn" value={formData.bn} onChange={handleBnChange} placeholder=" " maxLength={100} pattern="[A-Za-z0-9]+" title="Letters and numbers only" required disabled={saving} className={errors.bn ? 'ozui-error' : ''} />
                            <label htmlFor="bn"><FontAwesomeIcon icon={faTag} />{translations?.bn || 'BN'} <span style={{ color: '#dc2626' }}>*</span></label>
                        </div>
                        {errors.bn && <div className="ozui-table-error">{errors.bn}</div>}
                    </div>
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input type="date" name="date" id="date" value={formData.date} onChange={handleChange} placeholder=" " disabled={saving} />
                            <label htmlFor="date"><FontAwesomeIcon icon={faCalendarAlt} />{translations?.date || 'Date'}</label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field" ref={customerSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input ref={customerNameRef} type="text" name="customer_name" id="customer_name" value={formData.customer_name} onChange={handleChange} onFocus={() => formData.customer_name.length >= 2 && setShowCustomerDropdown(true)} placeholder=" " disabled={saving} autoComplete="off" data-no-scan="true" />
                            <label htmlFor="customer_name"><FontAwesomeIcon icon={faUser} />{translations?.customerName || 'Customer Name'}<span>({translations?.optional || 'Optional'})</span></label>
                            {searchingCustomer && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                        </div>
                        {showCustomerDropdown && customerSearchResults.length > 0 && (
                            <div className="ozui-customer-dropdown">
                                {customerSearchResults.map(customer => (
                                    <div key={customer.customerid} className="ozui-dropdown-item" onClick={() => selectCustomer(customer)}>
                                        <div className="ozui-customer-name">{customer.customer_name}</div>
                                        {customer.phone && <div className="ozui-customer-phone"><FontAwesomeIcon icon={faPhone} /> {customer.phone}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input type="tel" name="customer_phone" id="customer_phone" value={formData.customer_phone} onChange={handleChange} placeholder=" " disabled={saving} data-no-scan="true" />
                            <label htmlFor="customer_phone"><FontAwesomeIcon icon={faPhone} />{translations?.customerPhone || 'Phone Number'}<span>({translations?.optional || 'Optional'})</span></label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field" ref={productSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input type="text" id="product_search" value={productSearchTerm} onChange={(e) => { setProductSearchTerm(e.target.value); searchProducts(e.target.value); }} onFocus={() => productSearchTerm.length >= 2 && setShowProductDropdown(true)} placeholder=" " disabled={saving} autoComplete="off" data-no-scan="true" />
                            <label htmlFor="product_search"><FontAwesomeIcon icon={faSearch} />{translations?.searchProducts || 'Search Products'}</label>
                            {searchingProducts && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                        </div>
                        {showProductDropdown && filteredProducts.length > 0 && (
                            <div className="ozui-product-dropdown">
                                {filteredProducts.map(product => {
                                    const stock = computeAvailableStock(product);
                                    const isCentralized = product.is_product_centralized === 1;
                                    const isOwner = product.owner_shop_id == currentShop?.shop_id;
                                    const supplierName = product.supplier_name || '';
                                    const companyName = product.company || '';

                                    return (
                                        <div key={product.id} className="ozui-product-item" onClick={() => handleProductSelect(product)}>
                                            <div className="ozui-product-item-name" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                {product.product_name}
                                                {product.product_code && <span className="ozui-product-code-badge">{product.product_code}</span>}
                                                {isCentralized ? (
                                                    <span style={{
                                                        fontSize: '8px',
                                                        background: isOwner ? '#10b981' : '#6366f1',
                                                        color: 'white',
                                                        padding: '1px 6px',
                                                        borderRadius: '8px',
                                                        fontWeight: '600',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {isOwner ? '⭐ Owner' : '🌐 Shared'}
                                                    </span>
                                                ) : (
                                                    <span style={{
                                                        fontSize: '8px',
                                                        background: '#e5e7eb',
                                                        color: '#6b7280',
                                                        padding: '1px 6px',
                                                        borderRadius: '8px',
                                                        fontWeight: '600',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        Local
                                                    </span>
                                                )}
                                            </div>
                                            <div className="ozui-product-item-details" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '2px' }}>
                                                {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                                {product.gunit && <span><FontAwesomeIcon icon={faRuler} /> {product.gunit}</span>}
                                                {product.size && <span>Size: {product.size}</span>}
                                                {product.gauge && <span>Gauge: {product.gauge}</span>}
                                                {product.weight && <span>Wt: {product.weight}</span>}
                                                {product.class && <span>Cls: {product.class}</span>}
                                                {(supplierName || companyName) && (
                                                    <span style={{ color: '#059669' }}>
                                                        <FontAwesomeIcon icon={faUsers} /> {supplierName || companyName}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="ozui-product-item-price-stock" style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '2px' }}>
                                                <span className="ozui-product-price">
                                                    <FontAwesomeIcon icon={faRupeeSign} /> {Math.round(getProductSellPrice(product))}
                                                    <span style={{ fontWeight: '400', color: '#6b7280', fontSize: '9px', marginLeft: '2px' }}>
                                                        {product.sell_method === 'packing' ? '/pack' : '/unit'}
                                                    </span>
                                                </span>
                                                <span className={`ozui-product-stock ${stock <= (product.quantity_alert || 5) ? 'ozui-low-stock' : ''}`}>
                                                    Stock: {Math.round(stock)}
                                                    {isCentralized && !isOwner && (
                                                        <span style={{ fontSize: '8px', color: '#6366f1', marginLeft: '3px' }}>
                                                            (shared)
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <ItemsTable
                    items={formData.items}
                    onItemChange={handleItemChange}
                    onSelectProductForRow={handleRowProductSelect}
                    onRemove={removeItem}
                    onAdd={addItem}
                    errors={errors}
                    disabled={saving}
                    translations={translations}
                    isRTL={isRTL}
                    searchProductsAPI={fetchProductsBySearch}
                    showToast={showToast}
                    currentShop={currentShop}
                />

                {Array.isArray(formData.items) && formData.items.length > 0 && !isEditMode ? (
                    <div className="ozui-form-section">
                        <SalePaymentSection
                            payment={salePayment}
                            subtotal={formData.subtotal}
                            discount={formData.discount}
                            onDiscountChange={handleChange}
                            disabled={saving}
                            translations={translations}
                        />
                    </div>
                ) : Array.isArray(formData.items) && formData.items.length > 0 && isEditMode ? (
                    <div className="ozui-form-section">
                        <div className="ozui-calculations">
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.subtotal || 'Subtotal'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.subtotal}</span>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.discount || 'Discount'}:</span>
                                <div className="ozui-input-wrapper ozui-small-input">
                                    <input type="number" name="discount" value={formData.discount} onChange={handleChange} step="1" min="0" placeholder="0" disabled={saving} data-no-scan="true" />
                                </div>
                            </div>
                            <div className="ozui-calculation-row ozui-grand-total">
                                <span className="ozui-calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.grand_total}</span>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.paidAmount || 'Paid Amount'}:</span>
                                <div className="ozui-input-wrapper ozui-small-input">
                                    <input type="number" name="paid_amount" value={formData.paid_amount} onChange={handlePaidAmountChange} step="1" min="0" max={formData.grand_total} placeholder="0" disabled={saving} data-no-scan="true" />
                                </div>
                            </div>
                            <div className="ozui-calculation-row">
                                <span className="ozui-calculation-label">{translations?.balance || 'Balance'}:</span>
                                <span className="ozui-calculation-value">Rs. {formData.balance}</span>
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="ozui-form-actions">
                    <button ref={submitButtonRef} type="submit" className="ozui-btn ozui-btn-primary" disabled={saving || !Array.isArray(formData.items) || formData.items.length === 0}>
                        {saving ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /><span>{translations?.saving || 'Saving...'}</span></>
                        ) : (
                            <><FontAwesomeIcon icon={faSave} /><span>{isEditMode ? (translations?.updateSale || 'Update Sale') : (translations?.completeSale || 'Complete Sale')}</span></>
                        )}
                    </button>
                </div>
            </form>

            {loading && (
                <div className="ozui-loading-overlay">
                    <div className="ozui-loading-content">
                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}

            <style>{`
  .ozui-warranty-toggle {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 2px 5px;
    min-width: 85px;
    padding: 3px 6px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #f8fafc;
    color: #64748b;
    cursor: pointer;
    user-select: none;
  }
  .ozui-warranty-toggle.is-issued {
    border-color: #86efac;
    background: #f0fdf4;
    color: #15803d;
  }
  .ozui-warranty-toggle input { margin: 0; accent-color: #16a34a; }
  .ozui-warranty-toggle span { font-size: 9px; font-weight: 700; line-height: 1.2; }
  .ozui-warranty-toggle small { grid-column: 1 / -1; font-size: 8px; line-height: 1.2; overflow-wrap: anywhere; }
  .ozui-no-warranty { color: #9ca3af; font-size: 9px; }
  .ozui-product-code-badge {
    padding: 1px 5px;
    border-radius: 8px;
    background: #e5e7eb;
    color: #6b7280;
    font-size: 7px;
    font-weight: 600;
  }
  .ozui-product-item:hover {
    background-color: #f9fafb;
  }
  .ozui-scanner-status {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 9999;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 20px;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    font-size: 12px;
    pointer-events: none;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  .ozui-scanner-indicator { display: flex; align-items: center; gap: 6px; }
  .ozui-scanner-indicator.active { color: #10b981; }
  .ozui-scanner-indicator.inactive { color: #ef4444; }
  .ozui-rtl input[type="number"] {
    font-variant-numeric: lining-nums;
    font-feature-settings: "tnum";
  }
  .ozui-rtl .ozui-payment-value,
  .ozui-rtl .ozui-calculation-value {
    font-variant-numeric: lining-nums;
    font-feature-settings: "tnum";
  }
  @media (max-width: 768px) {
    .ozui-warranty-toggle { min-width: 70px; padding: 2px 4px; }
    .ozui-warranty-toggle span { font-size: 8px; }
    .ozui-warranty-toggle small { font-size: 7px; }
  }
`}</style>
        </div>
    );
};

export default SolarSpecialSell;
export { ItemsTable as SaleItemsTable, useBarcodeScanner, buildSaleItem };